<template>
    <PageHeader
        :logo-data="logoData"
        :logo-color="logoColor"
        :categories="categories"
        :categoriesTitle="$t('shared.categories')"
        :project-name="project.name"
        :project-status="projectStatus"
        :active-categories="activeCategories"
        @toggleCategory="$emit('toggleCategory', $event)"
    >
        <!-- $slots?.headerMiddle is needed here for proper css classes handle in @easy/ui -->
        <template v-if="$slots?.headerMiddle" #middle>
            <slot name="headerMiddle"></slot>
        </template>
        <template v-if="logged">
            <CStagingMarker />
            <cImpSession />
            <PageActionsButton v-if="!hasChanges && actions.length" :actions="actions" />

            <ButtonSecondaryMd
                v-if="isPublished(project) && shareLinks.length && !hideShare"
                @click="handleShare"
                class="!px-2.5 !2xl:px-2.5"
            >
                <ShareLinkIcon />
                <span class="hidden 2xl:block"> {{ $t("shared.share") }}</span>
            </ButtonSecondaryMd>
            <slot></slot>
            <!-- FIXME: what link should be here -->
            <a
                v-if="isPublished(project) && shareLinks.length"
                :href="shareLinks[0].url"
                target="_blank"
            >
                <ButtonSecondaryMd class="!px-2.5 !2xl:px-2.5">
                    <LinkShareIcon />
                    <span class="hidden 2xl:block">{{ $t("shared.preview") }}</span>
                </ButtonSecondaryMd>
            </a>
            <ButtonSecondaryMd
                v-if="!hidePublish"
                @click="handlePublish"
                :disabled="disabledForPublish"
            >
                <RocketIcon :class="{ 'text-orange-500': hasAnyToPublish }" />
                <span :class="{ 'text-orange-500': hasAnyToPublish }">
                    {{ hasAnyToPublish ? $t("shared.publish") : $t("shared.published") }}
                </span>
            </ButtonSecondaryMd>
            <cFireworks v-if="animationAfterPublish" />
        </template>
        <template v-else>
            <ButtonTertiaryMd @click="handleLogInForAnonymous">
                {{ $t("shared.log_in") }}
            </ButtonTertiaryMd>
            <ButtonPrimaryMd @click="handleRegisterForAnonymous">
                {{ $t("shared.sign_up") }}
            </ButtonPrimaryMd>
        </template>
    </PageHeader>
    <cModalShare v-if="shareLinks.length" v-model:open="showModalShare" :links="shareLinks">
        <template #extra>
            <slot name="share"></slot>
        </template>
    </cModalShare>
    <cModalHelp v-model="showModalHelp" />
    <cModalImpersonate v-model="showModalImpersonate" />
</template>

<script>
import { defineAsyncComponent } from "vue";
import { ButtonPrimaryMd, ButtonSecondaryMd, ButtonTertiaryMd } from "@easy/ui/buttons";
import { PageActionsButton, PageHeader } from "@easy/ui/page-components";
import {
    LinkShareIcon,
    RocketIcon,
    ShareLinkIcon,
    HomeIcon,
    TranslateIcon,
    SunIcon,
    HelpIcon,
    CogIcon,
    OpenHandIcon,
} from "@easy/ui/icons";
import { darkModeService } from "@/shared/themes/dark-mode.service";
import { EventBus } from "@/shared/projects/event-bus";
import { mapActions, mapState, mapGetters } from "vuex";
import cModalShare from "@/shared/components/cModalShare.vue";
import cModalHelp from "@/shared/components/cModalHelp.vue";
import cModalImpersonate from "@/shared/components/cModalImpersonate.vue";
import cImpSession from "@/shared/components/cImpSession.vue";
import { loginService } from "@/auth/service/login.service";
import { userService } from "@/shared/user/user.service";
import CStagingMarker from "@/shared/components/cStagingMarker.vue";

const cFireworks = defineAsyncComponent(() => import("@/shared/components/cFireworks.vue"));

export default {
    components: {
        CStagingMarker,
        PageHeader,
        ButtonSecondaryMd,
        ButtonTertiaryMd,
        ShareLinkIcon,
        LinkShareIcon,
        RocketIcon,
        ButtonPrimaryMd,
        PageActionsButton,
        cModalShare,
        cModalHelp,
        cModalImpersonate,
        cImpSession,
        cFireworks,
    },
    props: {
        actions: {
            type: Array,
            default: () => [],
        },
        logoColor: {
            type: String,
            default: "",
        },
        categories: {
            type: Array,
            default: () => [],
        },
        project: {
            type: Object,
            default: () => ({}),
        },
        hasChanges: {
            type: Boolean,
            default: false,
        },
        hasSnapshot: {
            type: Boolean,
            default: false,
        },
        shareLinks: {
            type: Array,
            default: () => [],
        },
        hideShare: {
            type: Boolean,
            default: false,
        },
        hidePublish: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["toggleCategory", "publish", "share"],
    data() {
        return {
            themeName: this.getThemeName(),
            showModalShare: false,
            showModalHelp: false,
            showModalImpersonate: false,
            animationAfterPublish: false,
        };
    },
    mounted() {
        EventBus.$on("darkMode", () => (this.themeName = this.getThemeName()));
        document.addEventListener("keydown", this.publishShortcut);
    },
    beforeUnmount() {
        document.removeEventListener("keydown", this.publishShortcut);
    },
    computed: {
        ...mapState(["lang"]),
        ...mapGetters([
            "logged",
            "isPublished",
            "activeAppDashboardLink",
            "settingsLink",
            "isAdmin",
            "isImp",
            "easyUrls",
        ]),
        hasAnyToPublish() {
            return this.hasChanges || this.hasSnapshot || !this.isPublished(this.project);
        },
        disabledForPublish() {
            return this.isPublished(this.project) && !(this.hasChanges || this.hasSnapshot);
        },
        logoData() {
            const items = [
                {
                    icon: HomeIcon,
                    label: this.$t("shared.go_to_home"),
                    handler: async () => {
                        const { magicUrl } = await userService.forgeMagicLink(this.easyUrls.cart);
                        window.location = magicUrl;
                    },
                },
                { isDivider: true },
                // {
                //     icon: MagnifyingGlassIcon,
                //     label: "Quick search",
                //     value: "⌘K",
                //     handler: () => {
                //     },
                // },
                {
                    icon: TranslateIcon,
                    label: this.$t("shared.language.label"),
                    value:
                        this.lang === "pl"
                            ? this.$t("shared.language.pl")
                            : this.$t("shared.language.en"),
                    handler: () => this.toggleLanguage(),
                },
                {
                    icon: SunIcon,
                    label: this.$t("shared.themes.label"),
                    // This theme should be considered inside global store for Observability !!!
                    value: this.themeName,
                    handler: () => this.toggleDarkMode(),
                },
                {
                    icon: HelpIcon,
                    label: this.$t("shared.help_community"),
                    handler: () => {
                        this.showModalHelp = true;
                    },
                },
                {
                    icon: CogIcon,
                    label: this.$t("shared.account_settings"),
                    handler: async () => {
                        const { magicUrl } = await userService.forgeMagicLink(this.settingsLink);
                        window.location = magicUrl;
                    },
                },
            ];

            if (this.isAdmin) {
                // Additional menu for admins:
                items.push({ isDivider: true });
                items.push({
                    icon: OpenHandIcon,
                    label: this.$t("shared.impersonate.menu"),
                    handler: () => {
                        this.showModalImpersonate = true;
                    },
                });
            }

            return {
                items,
                handler: async () => {
                    const { magicUrl } = await userService.forgeMagicLink(
                        this.activeAppDashboardLink
                    );
                    window.location = magicUrl;
                },
            };
        },
        projectStatus() {
            if (!this.isPublished(this.project)) {
                return { type: "warning", label: this.$t("shared.draft") };
            }
            if (this.hasChanges) {
                return { type: "neutral", label: this.$t("shared.unsaved") };
            }
            if (this.hasSnapshot) {
                return { type: "neutral", label: this.$t("shared.saved") };
            }

            return { type: "success", label: this.$t("shared.published") };
        },
        activeCategories() {
            const { name, meta } = this.$route;
            const { menuCategory } = meta || {};
            return menuCategory ? [menuCategory, name] : [name];
        },
    },
    methods: {
        ...mapActions(["updateLanguage"]),
        publishShortcut(e) {
            if (
                (e.metaKey && e.shiftKey && e.key === "p") ||
                (e.ctrlKey && e.shiftKey && e.key === "p")
            ) {
                this.$emit("publish");
            }
        },
        getThemeName() {
            return darkModeService.isCurrentModeDark()
                ? this.$t("shared.themes.dark")
                : this.$t("shared.themes.light");
        },
        toggleDarkMode() {
            darkModeService.toggleDarkMode();
            EventBus.$emit("darkMode", darkModeService.isCurrentModeDark());
        },
        async toggleLanguage() {
            await this.updateLanguage(this.lang === "pl" ? "en" : "pl");
        },
        handleLogInForAnonymous() {
            window.location = loginService.generateLogInLink(this.lang);
        },
        handleRegisterForAnonymous() {
            window.location = loginService.generateRegisterLink(this.lang);
        },
        handlePublish() {
            this.$emit("publish");
            this.animationAfterPublish = true;

            setTimeout(() => {
                this.animationAfterPublish = false;
            }, 1500);
        },
        handleShare() {
            this.$emit("share");
            this.showModalShare = true;
        },
    },
    watch: {
        lang() {
            this.themeName = this.getThemeName();
        },
    },
};
</script>
